import { useContext, useMemo } from 'react';
import { useQuery } from 'react-query';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import * as api from '../../services/api';
import { DATE_FORMAT, DAYS_OF_WEEK, QUERY_CACHE_TIME } from '../../constants';
import { SORT_BY_STRING } from '../../utils/sorting';

const useGenericSettings = () => {
  const { t } = useContext(ConfigContext);
  const storeKey = ['regions'];
  const { isLoading: isLoadingRegions, data: regions } = useQuery(storeKey, api.fetchRegions, {
    cacheTime: QUERY_CACHE_TIME
  });
  const industriesStoreKey = ['industries'];
  const { isLoading: isLoadingIndustry } = useQuery(industriesStoreKey, api.fetchIndustries, {
    cacheTime: QUERY_CACHE_TIME
  });

  const stringSorter = SORT_BY_STRING('label');

  const options = useMemo(
    () => ({
      currencies: regions?.currencies,
      countriesOptions: (regions?.countries || []).map(({ id, name }) => ({ value: id, label: name })),
      languagesOptions: (regions?.languages || [])
        .map(({ id, name }) => ({ value: id, label: name }))
        .sort(stringSorter),
      currenciesOptions: (regions?.currencies || []).map(({ id, symbol, name }) => ({
        value: id,
        label: `${symbol} - ${name}`
      })),
      hourFormatOptions: [
        { label: `12 ${t('hours')}`, value: '12' },
        { label: `24 ${t('hours')}`, value: '24' }
      ],
      accountStatuses: [
        { label: t('account_status__paying'), value: 'paying' },
        { label: t('account_status__trial'), value: 'trial' },
        { label: t('account_status__pilot'), value: 'pilot' },
        { label: t('account_status__frozen'), value: 'frozen' },
        { label: t('account_status__blocked'), value: 'blocked' },
        { label: t('account_status__unqualified'), value: 'unqualified' },
        { label: t('account_status__warning'), value: 'warning' },
        { label: t('account_status__staffingAgencyFreemium'), value: 'staffingAgencyFreemium' },
        { label: t('account_status__clientFreemium'), value: 'clientFreemium' },
        { label: t('account_status__internal'), value: 'internal' }
      ],
      dateFormatOptions: DATE_FORMAT.map((date) => ({ value: date, label: date })),
      firstDayOfWeekOptions: DAYS_OF_WEEK.map((day, index) => ({ value: index + 1, label: t(day) })),
      weekDaysOptions: DAYS_OF_WEEK.map((day, index) => ({ value: index + 1, label: t(day) })),
      distanceUnitOptions: [
        { label: t('distanceUnit_km'), value: 'km' },
        { label: t('distanceUnit_mi'), value: 'mi' }
      ]
    }),
    [regions?.countries, regions?.currencies, regions?.languages, stringSorter, t]
  );

  return { isLoading: isLoadingIndustry || isLoadingRegions, ...options };
};

export default useGenericSettings;
